import { ReactNode, useCallback, useEffect, useState } from 'react';
import {
	Badge,
	Box,
	Flex,
	IconButton,
	Image,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Skeleton,
	Text,
	useDisclosure,
	usePrefersReducedMotion
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { InformativoGet, listarInformativosDoMural } from '../../services/api';
import { ArteInformativo } from '../ArteInformativo';

const ROTULOS: Record<InformativoGet['tipo'], string> = {
	INFORMATIVO: 'Informativo',
	FERIADO: 'Feriado',
	DATA_COMEMORATIVA: 'Data comemorativa',
	NOVIDADE: 'Novidade'
};

const CORES: Record<InformativoGet['tipo'], string> = {
	INFORMATIVO: 'blue',
	FERIADO: 'orange',
	DATA_COMEMORATIVA: 'purple',
	NOVIDADE: 'green'
};

const INTERVALO_MS = 6000;

/**
 * A arte e a legenda têm alturas fixas para o banner não pular de tamanho a
 * cada troca de cartaz, nem empurrar o resto da página. No celular a legenda
 * empilha o tipo sobre o título, porque lado a lado o título mal teria lugar.
 */
const ALTURA_ARTE = { base: 160, md: 240 };
const ALTURA_LEGENDA = { base: 64, md: 56 };
const MARGEM = { base: 16, md: 24 };

const px = (valores: { base: number; md: number }) => ({ base: `${valores.base}px`, md: `${valores.md}px` });

/** Lado das setas (`size="sm"`), que ficam nas pontas da fileira de indicadores. */
const BOTAO = 32;

/**
 * Largura dos controles: as duas setas e os indicadores (o atual com 22px, os
 * outros com 8px), todos separados por 8px.
 */
const larguraControles = (total: number) => 2 * BOTAO + 22 + 8 * (total - 1) + 8 * (total + 1);

type MuralInformativosProps = {
	/**
	 * O que mostrar quando não há nada publicado. O portal do aluno passa o
	 * `Aviso` de boas-vindas, que era o que ocupava esse espaço antes; o
	 * dashboard não passa nada, e o mural simplesmente não ocupa lugar.
	 */
	fallback?: ReactNode;
};

/**
 * Banner de informativos publicado pelo ADMIN em `/dashboard/informativos`: um
 * cartaz por vez, passando sozinho a cada 6s. Aparece no topo do portal do
 * aluno e no topo do dashboard de admin e professor, acima dos números, dos
 * gráficos e das novidades.
 *
 * Ocupa a largura de quem o renderiza - o espaçamento é decisão da página.
 *
 * A arte aparece inteira, sem corte (ver `ArteInformativo`), e o tipo e o título
 * ficam numa faixa abaixo dela, não por cima: o recado está impresso no cartaz, e
 * a legenda sobreposta cobria justamente essa parte. Como o banner é baixo, o
 * clique abre a arte maior num modal, sem nada em volta. A troca automática
 * para enquanto o modal está aberto, com o mouse
 * sobre o banner ou com o foco dentro dele, para não puxar o cartaz debaixo de
 * quem está lendo.
 */
export const MuralInformativos = ({ fallback = null }: MuralInformativosProps) => {
	const [ informativos, setInformativos ] = useState<InformativoGet[]>([]);
	const [ carregando, setCarregando ] = useState(true);
	const [ atual, setAtual ] = useState(0);
	const [ pausado, setPausado ] = useState(false);
	const [ aberto, setAberto ] = useState<InformativoGet | null>(null);
	const modal = useDisclosure();
	const semAnimacao = usePrefersReducedMotion();

	const total = informativos.length;

	useEffect(() => {
		listarInformativosDoMural()
			.then((response) => setInformativos(response.data))
			// O mural é acessório: se falhar, o aluno ainda precisa ver as aulas.
			.catch(() => setInformativos([]))
			.finally(() => setCarregando(false));
	}, []);

	useEffect(
		() => {
			if (total < 2 || pausado || modal.isOpen || semAnimacao) return;

			const timer = setInterval(() => setAtual((indice) => (indice + 1) % total), INTERVALO_MS);
			return () => clearInterval(timer);
		},
		[ total, pausado, modal.isOpen, semAnimacao ]
	);

	const irPara = useCallback((indice: number) => setAtual((indice + total) % total), [ total ]);

	const abrir = (informativo: InformativoGet) => {
		setAberto(informativo);
		modal.onOpen();
	};

	if (carregando)
		return (
			<Box width="100%" flexShrink={0}>
				<Skeleton
					height={{
						base: `${ALTURA_ARTE.base + ALTURA_LEGENDA.base}px`,
						md: `${ALTURA_ARTE.md + ALTURA_LEGENDA.md}px`
					}}
					borderRadius="16px"
				/>
			</Box>
		);

	if (total === 0) return <>{fallback}</>;

	// O título não pode passar por baixo dos controles, que ficam na ponta
	// direita da legenda.
	const folga = total > 1 ? larguraControles(total) + 16 : 0;

	return (
		<>
			<Box
				width="100%"
				// Como item de flex, o `overflow: hidden` zera a altura mínima
				// automática do banner: sem isto o dashboard o esmaga a poucos
				// pixels para caber o painel de novidades.
				flexShrink={0}
				position="relative"
				borderRadius="16px"
				overflow="hidden"
				boxShadow="md"
				bgColor="blue.900"
				role="region"
				aria-roledescription="carrossel"
				aria-label="Informativos da escola"
				onMouseEnter={() => setPausado(true)}
				onMouseLeave={() => setPausado(false)}
				onFocus={() => setPausado(true)}
				onBlur={() => setPausado(false)}
			>
				<Flex
					transition={semAnimacao ? undefined : 'transform .5s ease'}
					transform={`translateX(-${atual * 100}%)`}
				>
					{informativos.map((informativo, indice) => (
						<Box
							key={informativo.id}
							as="button"
							type="button"
							minWidth="100%"
							display="flex"
							flexDir="column"
							textAlign="left"
							cursor="pointer"
							// O slide fora de vista sai da ordem de tabulação para o
							// Tab não passear por cartazes que ninguém está vendo.
							tabIndex={indice === atual ? 0 : -1}
							aria-hidden={indice !== atual}
							aria-label={`Abrir informativo ${informativo.titulo}`}
							onClick={() => abrir(informativo)}
						>
							<ArteInformativo
								src={informativo.imagemUrl}
								alt={informativo.titulo}
								width="100%"
								height={px(ALTURA_ARTE)}
							/>
							<Flex
								width="100%"
								height={px(ALTURA_LEGENDA)}
								flexDir={{ base: 'column', md: 'row' }}
								align={{ base: 'flex-start', md: 'center' }}
								justify={{ base: 'center', md: 'flex-start' }}
								gap={{ base: '4px', md: '12px' }}
								paddingLeft={px(MARGEM)}
								paddingRight={{ base: `${MARGEM.base + folga}px`, md: `${MARGEM.md + folga}px` }}
							>
								<Badge colorScheme={CORES[informativo.tipo]} flexShrink={0}>
									{ROTULOS[informativo.tipo]}
								</Badge>
								<Text
									// O título inteiro está no `aria-label` do slide e no
									// cabeçalho do modal; aqui ele cede para não mudar a
									// altura do banner.
									noOfLines={1}
									maxWidth="100%"
									color="white"
									fontFamily="heading"
									fontWeight="bold"
									fontSize={{ base: '17px', md: '20px' }}
									lineHeight="1.3"
								>
									{informativo.titulo}
								</Text>
							</Flex>
						</Box>
					))}
				</Flex>

				{total > 1 && (
					// Na ponta direita da legenda, centrados na altura dela. Nada fica
					// sobre a arte: nas pontas de um banner largo também há recado.
					<Flex
						position="absolute"
						right={px(MARGEM)}
						bottom={{
							base: `${(ALTURA_LEGENDA.base - BOTAO) / 2}px`,
							md: `${(ALTURA_LEGENDA.md - BOTAO) / 2}px`
						}}
						height={`${BOTAO}px`}
						align="center"
						gap="8px"
					>
						<IconButton
							aria-label="Informativo anterior"
							icon={<ChevronLeftIcon boxSize="24px" />}
							borderRadius="full"
							size="sm"
							variant="ghost"
							color="white"
							_hover={{ bgColor: 'whiteAlpha.200' }}
							_active={{ bgColor: 'whiteAlpha.300' }}
							onClick={() => irPara(atual - 1)}
						/>
						{informativos.map((informativo, indice) => (
							<Box
								key={informativo.id}
								as="button"
								type="button"
								height="8px"
								width={indice === atual ? '22px' : '8px'}
								borderRadius="full"
								transition="width .3s ease, background-color .3s ease"
								bgColor={indice === atual ? 'brass.400' : 'whiteAlpha.700'}
								aria-label={`Ver informativo ${indice + 1} de ${total}`}
								aria-current={indice === atual}
								onClick={() => irPara(indice)}
							/>
						))}
						<IconButton
							aria-label="Próximo informativo"
							icon={<ChevronRightIcon boxSize="24px" />}
							borderRadius="full"
							size="sm"
							variant="ghost"
							color="white"
							_hover={{ bgColor: 'whiteAlpha.200' }}
							_active={{ bgColor: 'whiteAlpha.300' }}
							onClick={() => irPara(atual + 1)}
						/>
					</Flex>
				)}
			</Box>

			{/* O modal existe para a arte: nada de texto embaixo dela disputando a
			    altura, e a largura maior que a do corpo padrão para o cartaz chegar
			    perto do tamanho em que foi desenhado. */}
			<Modal isOpen={modal.isOpen} onClose={modal.onClose} size="5xl" isCentered>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader paddingRight="48px">{aberto?.titulo}</ModalHeader>
					<ModalCloseButton />
					<ModalBody paddingBottom="24px">
						{aberto && (
							<Image
								src={aberto.imagemUrl}
								alt={aberto.titulo}
								width="100%"
								maxHeight="78vh"
								objectFit="contain"
								borderRadius="10px"
							/>
						)}
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
};
